<template>
  <div class="bg-[#f7f8fb] text-neutral-800 antialiased relative" style="font-family: Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;">
    <BackgroundTextures />
    <AnimatedBackground />

    <UIUXHero 
      :name="name" 
      :portrait-image="portraitImage" 
      :cv-url="cvUrl"
      :cv-download-url="cvDownloadUrl"
    />
    
    <UIUXIntroduction 
      :introduction-text="introductionText"
      :studio-image="studioImage"
      :skills="skills"
    />
    
    <!-- Skewed Divider -->
    <div class="relative overflow-hidden" aria-hidden="true">
      <div class="-skew-y-2 bg-white ring-1 ring-neutral-200 py-12"></div>
    </div>
    
    <UIUXEducation :education="education" />
    
    <UIUXWork
      section-id="uiux"
      title="UI/UX"
      :projects="uiuxProjects"
      :processes="processes"
      :show-process="true"
    />

    <UIUXWork
      section-id="graphics"
      title="Graphics"
      :projects="graphicsProjects"
      :show-process="false"
    />
    
    <UIUXContact 
      :contact-text="contactText"
      :contacts="contacts"
    />
    
    <UIUXFooter :name="name" />
  </div>
</template>

<script>
import BackgroundTextures from '../components/uiux/BackgroundTextures.vue'
import AnimatedBackground from '../components/uiux/AnimatedBackground.vue'
import UIUXHero from '../components/uiux/UIUXHero.vue'
import UIUXIntroduction from '../components/uiux/UIUXIntroduction.vue'
import UIUXEducation from '../components/uiux/UIUXEducation.vue'
import UIUXWork from '../components/uiux/UIUXWork.vue'
import UIUXContact from '../components/uiux/UIUXContact.vue'
import UIUXFooter from '../components/uiux/UIUXFooter.vue'
import uiuxProjectsData from '../data/uiuxProjects.json'
import graphicsProjectsData from '../data/graphicsProjects.json'

const uiuxThumbnails = require.context('../assets/projects/UIUX', false, /\.(png|jpe?g|webp)$/i)
const graphicsThumbnails = require.context('../assets/projects/Graphics', false, /\.(png|jpe?g|webp)$/i)

function resolveUiuxThumbnail(filename) {
  try {
    return uiuxThumbnails(`./${filename}`)
  } catch (e) {
    console.error('UI/UX thumbnail not found:', filename)
    return ''
  }
}

function resolveGraphicsThumbnail(filename) {
  try {
    return graphicsThumbnails(`./${filename}`)
  } catch (e) {
    console.error('Graphics thumbnail not found:', filename)
    return ''
  }
}

export default {
  name: 'GraphicsPortfolio',
  components: {
    BackgroundTextures,
    AnimatedBackground,
    UIUXHero,
    UIUXIntroduction,
    UIUXEducation,
    UIUXWork,
    UIUXContact,
    UIUXFooter
  },
  data() {
    return {
      name: 'Muhammad Saad Mehboob',
      portraitImage: require('../assets/Untitled design (13).png'),
      cvUrl: 'https://drive.google.com/file/d/1S3yxqUeBYBksyPFRdQ_i81WgaOuh2IV2/preview',
      cvDownloadUrl: 'https://drive.google.com/uc?export=download&id=1S3yxqUeBYBksyPFRdQ_i81WgaOuh2IV2',
      introductionText: 'I craft thoughtful digital experiences that blend utility, clarity, and emotion. My process orbits research, rapid prototyping, and tasteful visual systems that scale. Below is a concise snapshot of recent work spanning product design, creative direction, and experimental visuals.',
      studioImage: require('../assets/Untitled design (13).png'),
      skills: [
        { name: 'Figma', icon: 'Figma', iconColor: 'text-pink-500' },
        { name: 'Prototyping', icon: 'MousePointer2', iconColor: 'text-emerald-600' },
        { name: 'Design Systems', icon: 'LayoutDashboard', iconColor: 'text-sky-600' }
      ],
      education: [
        {
          title: 'BS Software Engineering',
          institution: 'FAST Islamabad • Ongoing',
          description: 'Foundations in algorithms, systems, and product-minded engineering.',
          icon: 'GraduationCap',
          iconColor: 'text-emerald-600',
          bgColor: 'bg-emerald-100'
        },
        {
          title: 'UI/UX Professional Certificate',
          institution: 'Issued by Meta',
          description: 'End‑to‑end UX process: research, flows, wireframes, prototyping, and testing.',
          icon: 'BadgeCheck',
          iconColor: 'text-sky-600',
          bgColor: 'bg-sky-100'
        },
        {
          title: 'Graphic Design 101',
          institution: 'Certification — Canva',
          description: 'Visual hierarchy, color theory, typography, and layout essentials.',
          icon: 'Palette',
          iconColor: 'text-pink-600',
          bgColor: 'bg-pink-100'
        },
        {
          title: 'Marketing with Canva',
          institution: 'Certification — Canva',
          description: 'Content strategy, brand assets, and multi‑channel creative production.',
          icon: 'Megaphone',
          iconColor: 'text-amber-600',
          bgColor: 'bg-amber-100'
        }
      ],
      uiuxProjects: uiuxProjectsData.map((project) => ({
        ...project,
        image: resolveUiuxThumbnail(project.thumbnail)
      })),
      graphicsProjects: graphicsProjectsData.map((project) => ({
        ...project,
        image: resolveGraphicsThumbnail(project.thumbnail)
      })),
      processes: [
        {
          title: 'Discovery',
          description: 'Research, insights, synthesis',
          icon: 'Search',
          iconColor: 'text-emerald-600'
        },
        {
          title: 'Design',
          description: 'Flows, wireframes, systems',
          icon: 'Presentation',
          iconColor: 'text-sky-600'
        },
        {
          title: 'Delivery',
          description: 'Prototypes, specs, QA',
          icon: 'Rocket',
          iconColor: 'text-amber-600'
        }
      ],

      contactText: 'Currently open for freelance and collabs. Share a short brief and timeline.',
      contacts: [
        {
          label: 'muhammadsaadmeh824@gmail.com',
          type: 'Email',
          url: 'mailto:muhammadsaadmeh824@gmail.com',
          icon: 'Mail',
          iconColor: 'text-emerald-600',
          bgColor: 'bg-emerald-100'
        },
        {
          label: 'LinkedIn',
          type: 'Connect',
          url: 'https://www.linkedin.com/in/muhammad-saad-mehboob-125829247/',
          icon: 'Linkedin',
          iconColor: 'text-sky-600',
          bgColor: 'bg-sky-100'
        },
        {
          label: 'Behance',
          type: 'Portfolio',
          url: 'https://www.behance.net/gallery/224061971/Portfolio',
          icon: 'Dribbble',
          iconColor: 'text-pink-600',
          bgColor: 'bg-pink-100'
        }
      ]
    }
  },
  mounted() {
    // Smooth scroll for internal anchors
    document.querySelectorAll('a[href^="#"]').forEach(a => {
      a.addEventListener('click', e => {
        const id = a.getAttribute('href')
        if (id && id.length > 1 && document.querySelector(id)) {
          e.preventDefault()
          document.querySelector(id).scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    })
  }
}
</script>

<style scoped>
/* Additional styles if needed */
</style>
